import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AppTableColumn } from '../../_models/models';
import { ColumnPreferencesService } from '../../_services/column-preferences.service';

interface ColumnGroupKey {
  key: string;
  label: string;
}

interface ColumnGroupViewModel {
  key: string;
  label: string;
  columns: AppTableColumn[];
  badge: string;
  allSelected: boolean;
  hasNoResults: boolean;
}

@Component({
  selector: 'app-customize-columns',
  templateUrl: './app-customize-columns.component.html',
  styleUrls: ['./app-customize-columns.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomizeColumnsComponent {
  @Input() columns: AppTableColumn[] = [];
  @Input() storageKey = '';
  @Input() groupLabels: Record<string, string> = {};
  @Output() columnsChange = new EventEmitter<AppTableColumn[]>();

  isOpen = false;
  search = '';
  collapsed: Record<string, boolean> = {};
  groups: ColumnGroupViewModel[] = [];

  private draft: AppTableColumn[] = [];

  constructor(private columnPrefs: ColumnPreferencesService) {}

  get selectedCount(): number {
    return this.draft.filter(c => !c.isHidden).length;
  }

  get totalCount(): number {
    return this.draft.length;
  }

  get saveDisabled(): boolean {
    return this.selectedCount === 0;
  }

  openPanel(): void {
    this.draft = this.columns.filter(c => c.columnGroup).map(c => ({ ...c }));
    this.search = '';
    this.isOpen = true;
    this.refreshGroups();
  }

  cancel(): void {
    this.isOpen = false;
  }

  save(): void {
    if (this.saveDisabled) {
      return;
    }
    const draftMap = new Map(this.draft.map(d => [d.id, d.isHidden]));
    const updatedColumns = this.columns.map(col =>
      draftMap.has(col.id) ? { ...col, isHidden: draftMap.get(col.id) ?? col.isHidden } : col
    );
    this.columnPrefs.persist(this.storageKey, updatedColumns);
    this.columnsChange.emit(updatedColumns);
    this.isOpen = false;
  }

  onSearchChange(value: string): void {
    this.search = value;
    this.refreshGroups();
  }

  toggleGroup(key: string): void {
    this.collapsed[key] = !this.collapsed[key];
  }

  toggleAllInGroup(key: string, event: MouseEvent): void {
    event.stopPropagation();
    const allVisible = this.draft.filter(c => c.columnGroup === key).every(c => !c.isHidden);
    this.draft = this.draft.map(c =>
      c.columnGroup === key ? { ...c, isHidden: allVisible } : c
    );
    this.refreshGroups();
  }

  toggleColumn(id: string | number): void {
    this.draft = this.draft.map(c =>
      c.id === id ? { ...c, isHidden: !c.isHidden } : c
    );
    this.refreshGroups();
  }

  trackByGroupKey(_index: number, group: ColumnGroupViewModel): string {
    return group.key;
  }

  trackByColumnId(_index: number, column: AppTableColumn): string | number {
    return column.id;
  }

  private refreshGroups(): void {
    const query = this.search.trim().toLowerCase();
    this.groups = this.collectGroupKeys().map(group => this.buildGroupViewModel(group, query));
  }

  private buildGroupViewModel({ key, label }: ColumnGroupKey, query: string): ColumnGroupViewModel {
    const groupColumns = this.draft.filter(c => c.columnGroup === key);
    const filteredColumns = query
      ? groupColumns.filter(c => c.displayName.toLowerCase().includes(query))
      : groupColumns;
    const visibleCount = groupColumns.filter(c => !c.isHidden).length;
    return {
      key,
      label,
      columns: filteredColumns,
      badge: `${visibleCount}/${groupColumns.length}`,
      allSelected: groupColumns.every(c => !c.isHidden),
      hasNoResults: filteredColumns.length === 0 && !!query,
    };
  }

  private collectGroupKeys(): ColumnGroupKey[] {
    const seen = new Set<string>();
    const groups: ColumnGroupKey[] = [];
    for (const col of this.draft) {
      if (col.columnGroup && !seen.has(col.columnGroup)) {
        seen.add(col.columnGroup);
        groups.push({ key: col.columnGroup, label: this.groupLabels?.[col.columnGroup] ?? col.columnGroup });
      }
    }
    return groups;
  }
}
